import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Megaphone, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import Chip from '../components/Chip';
import ConfirmationDialog from '../components/ConfirmationDialog';
import SearchField from '../components/SearchField';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
} from '../components/StandardTable';
import { usePageTitle } from '../hooks/usePageTitle';
import api from '../lib/api';
import RefreshButton from '../components/RefreshButton';
import {
  Announcement,
  AudienceOption,
  AudiencePreview,
  buildAudienceSelectionValue,
  ClientOrganizationSearchResult,
  getAnnouncementAudienceLabel,
  getAudienceOptionDescription,
  getAudienceOptionLabel,
  mergeAudienceOptions,
  normalizeAnnouncements,
  normalizeAudienceOption,
  normalizeAudiencePreview,
  normalizeClientOrganizationSearchResults,
  parseAudienceSelectionValue,
} from '../utils/announcementNormalizers';

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  completed: 'success',
  running: 'info',
  pending: 'warning',
  failed: 'danger',
  cancelled: 'neutral',
};

const AdminAnnouncementsPage: React.FC = () => {
  usePageTitle('Client Announcements', 'Post in-app notifications to targeted clients');

  const [audienceOptions, setAudienceOptions] = useState<AudienceOption[]>(() => mergeAudienceOptions([], []));
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState<AudiencePreview | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [audienceSelection, setAudienceSelection] = useState('calendar_email_subscribers');
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<string[]>([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgResults, setOrgResults] = useState<ClientOrganizationSearchResult[]>([]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [audiencesResult, announcementsResult] = await Promise.allSettled([
        api.get('/admin/announcements/audiences'),
        api.get('/admin/announcements?limit=20'),
      ]);

      if (audiencesResult.status === 'fulfilled') {
        const predefined = (audiencesResult.value.data.data.predefined || []).map((row: Record<string, unknown>) =>
          normalizeAudienceOption(row),
        );
        const customGroups = (audiencesResult.value.data.data.custom_groups || []).map((row: Record<string, unknown>) =>
          normalizeAudienceOption(row),
        );
        setAudienceOptions(mergeAudienceOptions(predefined, customGroups));
      } else {
        setAudienceOptions(mergeAudienceOptions([], []));
        toast.error('Could not refresh audience counts. Predefined segments are still available.');
      }

      if (announcementsResult.status === 'fulfilled') {
        setAnnouncements(normalizeAnnouncements(announcementsResult.value.data.data.announcements || []));
      } else {
        toast.error('Failed to load announcement history');
      }
    } catch {
      toast.error('Failed to load announcement data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (audienceSelection !== 'selected_organizations') return undefined;

    const timer = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          limit: '20',
          ...(orgSearch.trim() && { q: orgSearch.trim() }),
        });
        const response = await api.get(`/admin/audience-groups/client-organizations/search?${params.toString()}`);
        setOrgResults(normalizeClientOrganizationSearchResults(response.data.data?.organizations || []));
      } catch {
        setOrgResults([]);
        toast.error('Failed to search client organizations');
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [audienceSelection, orgSearch]);

  const audiencePayload = useMemo(() => {
    const parsed = parseAudienceSelectionValue(audienceSelection);
    if (parsed.audienceType === 'selected_organizations') {
      return {
        audienceType: parsed.audienceType,
        audienceConfig: { organization_ids: selectedOrganizationIds },
      };
    }
    return parsed;
  }, [audienceSelection, selectedOrganizationIds]);

  const predefinedOptions = useMemo(
    () => audienceOptions.filter((option) => option.isPredefined && option.audienceType !== 'selected_organizations'),
    [audienceOptions],
  );
  const savedGroupOptions = useMemo(
    () => audienceOptions.filter((option) => option.audienceType === 'custom_group'),
    [audienceOptions],
  );
  const oneTimeOption = useMemo(
    () => audienceOptions.find((option) => option.audienceType === 'selected_organizations'),
    [audienceOptions],
  );

  const selectedAudienceOption = useMemo(() => {
    return audienceOptions.find((option) => buildAudienceSelectionValue(option) === audienceSelection);
  }, [audienceOptions, audienceSelection]);

  const previewAudienceLabel = useMemo(() => {
    if (audiencePreview?.audienceSummary.label) {
      return audiencePreview.audienceSummary.label;
    }
    if (selectedAudienceOption?.label) {
      return selectedAudienceOption.label;
    }
    const groupId = audiencePayload.audienceConfig.group_id as string | undefined;
    return getAudienceOptionLabel(audiencePayload.audienceType, groupId, audienceOptions);
  }, [audiencePreview, selectedAudienceOption, audiencePayload, audienceOptions]);

  const previewAudienceDescription = useMemo(() => {
    if (audiencePreview?.audienceSummary.description) {
      return audiencePreview.audienceSummary.description;
    }
    if (selectedAudienceOption?.description) {
      return selectedAudienceOption.description;
    }
    return getAudienceOptionDescription(audiencePayload.audienceType, audienceOptions);
  }, [audiencePreview, selectedAudienceOption, audiencePayload, audienceOptions]);

  const previewAudience = useCallback(async () => {
    if (
      audiencePayload.audienceType === 'selected_organizations'
      && selectedOrganizationIds.length === 0
    ) {
      setAudiencePreview(null);
      return;
    }

    setIsPreviewLoading(true);
    try {
      const response = await api.post('/admin/announcements/preview', {
        audience_type: audiencePayload.audienceType,
        audience_config: audiencePayload.audienceConfig,
        sample_limit: 500,
      });
      setAudiencePreview(normalizeAudiencePreview(response.data.data));
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'Failed to preview audience');
      setAudiencePreview(null);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [audiencePayload, selectedOrganizationIds.length]);

  useEffect(() => {
    setAudiencePreview(null);
  }, [audienceSelection, selectedOrganizationIds]);

  useEffect(() => {
    if (audiencePayload.audienceType === 'selected_organizations' && selectedOrganizationIds.length === 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      previewAudience();
    }, 400);

    return () => window.clearTimeout(timer);
  }, [audiencePayload, previewAudience, selectedOrganizationIds.length]);

  const toggleOrganization = (orgId: string) => {
    setSelectedOrganizationIds((current) =>
      current.includes(orgId) ? current.filter((id) => id !== orgId) : [...current, orgId],
    );
  };

  const sendAnnouncement = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    setIsSending(true);
    try {
      await api.post('/admin/announcements', {
        title: title.trim(),
        message: message.trim(),
        audience_type: audiencePayload.audienceType,
        audience_config: audiencePayload.audienceConfig,
      });

      toast.success('Announcement queued for delivery');
      setTitle('');
      setMessage('');
      setAudiencePreview(null);
      setConfirmOpen(false);
      await loadData();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'Failed to send announcement');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary dark:text-text-dark-primary">
            Client Announcements
          </h1>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
            Notify targeted clients with in-app notifications (bell widget and mobile push when enabled).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/audience-groups">
            <Button variant="secondary">Manage Groups</Button>
          </Link>
          <RefreshButton onRefresh={loadData} />
        </div>
      </div>

      <div className="card p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-primary-700 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            Compose Announcement
          </h2>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
            Title *
          </label>
          <input
            className="input w-full"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Calendar digest delayed"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
            Message *
          </label>
          <textarea
            className="input w-full min-h-[120px]"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="The nightly calendar digest will be delayed today due to changes on the state website."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
            Audience
          </label>
          <p className="text-xs text-text-secondary dark:text-text-dark-secondary mb-2">
            Choose who receives this announcement. Saved groups are reusable lists you manage under{' '}
            <Link to="/admin/audience-groups" className="text-primary-700 dark:text-primary-400 hover:underline">
              Audience Groups
            </Link>
            . Predefined segments are built-in filters; one-time selection applies only to this send.
          </p>
          <select
            className="input w-full text-text-primary dark:text-text-dark-primary"
            value={audienceSelection}
            onChange={(event) => setAudienceSelection(event.target.value)}
          >
            <optgroup label="Predefined segments">
              {predefinedOptions.map((option) => (
                <option key={buildAudienceSelectionValue(option)} value={buildAudienceSelectionValue(option)}>
                  {option.label}
                  {option.totalRecipients > 0 ? ` (${option.totalRecipients})` : ''}
                </option>
              ))}
            </optgroup>
            {savedGroupOptions.length > 0 && (
              <optgroup label="Saved groups">
                {savedGroupOptions.map((option) => (
                  <option key={buildAudienceSelectionValue(option)} value={buildAudienceSelectionValue(option)}>
                    {option.label}
                    {option.totalRecipients > 0 ? ` (${option.totalRecipients})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {oneTimeOption && (
              <optgroup label="One-time selection">
                <option value={buildAudienceSelectionValue(oneTimeOption)}>
                  {oneTimeOption.label}
                </option>
              </optgroup>
            )}
          </select>
          {previewAudienceDescription && (
            <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-2">
              {previewAudienceDescription}
            </p>
          )}
          {selectedAudienceOption?.organizationCount != null && selectedAudienceOption.organizationCount > 0 && (
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
              {selectedAudienceOption.organizationCount} organization
              {selectedAudienceOption.organizationCount === 1 ? '' : 's'} in this group
            </p>
          )}
        </div>

        {audienceSelection === 'selected_organizations' && (
          <div className="space-y-3">
            <SearchField
              value={orgSearch}
              onChange={setOrgSearch}
              placeholder="Search client organizations (paid or trial)..."
            />
            <div className="border border-border-light dark:border-border-dark rounded-lg max-h-48 overflow-y-auto">
              {orgResults.map((org) => (
                <label
                  key={org.id}
                  className="flex items-center gap-3 px-4 py-3 border-b border-border-light dark:border-border-dark last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedOrganizationIds.includes(org.id)}
                    onChange={() => toggleOrganization(org.id)}
                    className="h-4 w-4 rounded border-border-light dark:border-border-dark text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      {org.name}
                    </span>
                    <span className="block text-xs text-text-secondary dark:text-text-dark-secondary">
                      {org.orgCategory} · {org.subscription}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              icon={Users}
              onClick={previewAudience}
              disabled={isPreviewLoading}
            >
              {isPreviewLoading ? 'Refreshing preview...' : 'Refresh Preview'}
            </Button>
            {audiencePreview != null && (
              <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {audiencePreview.totalRecipients.toLocaleString()} recipient
                {audiencePreview.totalRecipients === 1 ? '' : 's'}
              </span>
            )}
            <Button
              variant="primary"
              icon={Megaphone}
              onClick={() => setConfirmOpen(true)}
              disabled={isSending || audiencePreview == null || audiencePreview.totalRecipients === 0}
            >
              Send Announcement
            </Button>
          </div>

          {isPreviewLoading && audiencePreview == null && (
            <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading recipient preview...
            </div>
          )}

          {audiencePreview != null && (
            <div className="rounded-lg border border-border-light dark:border-border-dark p-4 space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                  Recipient preview
                </h3>
                <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                  {audiencePreview.totalRecipients.toLocaleString()} recipient
                  {audiencePreview.totalRecipients === 1 ? '' : 's'} for{' '}
                  <span className="font-medium text-text-primary dark:text-text-dark-primary">
                    {previewAudienceLabel}
                  </span>
                  . Scroll the table to browse
                  {audiencePreview.sampleRecipients.length < audiencePreview.totalRecipients
                    ? ` (showing ${audiencePreview.sampleRecipients.length.toLocaleString()} loaded)`
                    : ''}
                  .
                </p>
              </div>

              {audiencePreview.totalRecipients === 0 ? (
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  No recipients match this audience. Choose a different segment or add organizations.
                </p>
              ) : audiencePreview.sampleRecipients.length === 0 ? (
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  Recipients resolved, but no sample could be loaded.
                </p>
              ) : (
                <div className="max-h-72 overflow-y-auto rounded-lg border border-border-light dark:border-border-dark">
                  <StandardTable horizontalScroll variant="compact" stickyHeader className="border-0 shadow-none rounded-none">
                    <StandardTableHeader>
                      <StandardTableRow>
                        <StandardTableHeaderCell>Name</StandardTableHeaderCell>
                        <StandardTableHeaderCell>Email</StandardTableHeaderCell>
                        <StandardTableHeaderCell>Organizations</StandardTableHeaderCell>
                      </StandardTableRow>
                    </StandardTableHeader>
                    <StandardTableBody>
                      {audiencePreview.sampleRecipients.map((recipient) => (
                        <StandardTableRow key={recipient.userId} hoverable={false}>
                          <StandardTableCell>
                            <span className="text-sm text-text-primary dark:text-text-dark-primary">
                              {recipient.displayName}
                            </span>
                          </StandardTableCell>
                          <StandardTableCell>
                            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                              {recipient.email || '—'}
                            </span>
                          </StandardTableCell>
                          <StandardTableCell>
                            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                              {recipient.organizationNames.length > 0
                                ? recipient.organizationNames.join(', ')
                                : '—'}
                            </span>
                          </StandardTableCell>
                        </StandardTableRow>
                      ))}
                    </StandardTableBody>
                  </StandardTable>
                </div>
              )}

              {audiencePreview.totalRecipients > audiencePreview.sampleRecipients.length && (
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {(
                    audiencePreview.totalRecipients - audiencePreview.sampleRecipients.length
                  ).toLocaleString()} more recipients exist but were not loaded in this preview.
                </p>
              )}
            </div>
          )}

          {audiencePayload.audienceType === 'selected_organizations' && selectedOrganizationIds.length === 0 && (
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Select at least one organization to preview recipients.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="p-6 border-b border-border-light dark:border-border-dark">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            Recent Announcements
          </h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-text-secondary dark:text-text-dark-secondary">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading history...
          </div>
        ) : announcements.length === 0 ? (
          <div className="py-12 text-center text-text-secondary dark:text-text-dark-secondary">
            No announcements sent yet.
          </div>
        ) : (
          <StandardTable horizontalScroll>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Title</StandardTableHeaderCell>
                <StandardTableHeaderCell>Audience</StandardTableHeaderCell>
                <StandardTableHeaderCell>Status</StandardTableHeaderCell>
                <StandardTableHeaderCell>Recipients</StandardTableHeaderCell>
                <StandardTableHeaderCell>Sent</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {announcements.map((announcement) => (
                <StandardTableRow key={announcement.id}>
                  <StandardTableCell>
                    <div className="font-medium text-text-primary dark:text-text-dark-primary">
                      {announcement.title}
                    </div>
                    <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1 line-clamp-2">
                      {announcement.message}
                    </div>
                  </StandardTableCell>
                  <StandardTableCell>
                    {getAnnouncementAudienceLabel(
                      announcement.audienceType,
                      announcement.audienceConfig,
                      audienceOptions,
                    )}
                  </StandardTableCell>
                  <StandardTableCell>
                    <Chip
                      label={announcement.status}
                      variant={STATUS_VARIANTS[announcement.status] || 'neutral'}
                      size="sm"
                    />
                  </StandardTableCell>
                  <StandardTableCell>
                    {announcement.totalRecipients}
                    {announcement.inAppCreated > 0 ? ` · ${announcement.inAppCreated} notified` : ''}
                  </StandardTableCell>
                  <StandardTableCell>
                    {announcement.createdAt
                      ? new Date(announcement.createdAt).toLocaleString()
                      : '—'}
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}
      </div>

      <ConfirmationDialog
        isOpen={confirmOpen}
        title="Send announcement?"
        message={`This will send in-app notifications to ${audiencePreview != null ? audiencePreview.totalRecipients.toLocaleString() : 'the selected'} recipient(s).`}
        confirmText={isSending ? 'Sending...' : 'Send'}
        variant="warning"
        onConfirm={sendAnnouncement}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default AdminAnnouncementsPage;
